import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  FormControl,
  Modal,
  ProgressBar,
  Row,
} from "react-bootstrap";
import {
  ClockFill,
  EnvelopeFill,
  Eye,
  FileFill,
  Pen,
} from "react-bootstrap-icons";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FormGenerator from "../form-generator/FormGenerator";
import { NumberInput, TextInput } from "../form-generator/components";
import {
  highlightError,
  setAttribute,
} from "../form-generator/helpers/utility";
import { makeApiRequests, makeRESTApiRequests } from "../helpers/api";
import { ENDPOINTS, labelToKeyMap, surveyEditForm } from "../helpers/constants";
import {
  createMapsLinkFromJobs,
  fillJobVariablesInText,
} from "../helpers/global";
import useAppChoices from "../hooks/useAppChoices";
import useAuth from "../hooks/useAuth";
import Loader from "./Loader";
import NotFound from "./NotFound";
import ResearchSection from "./ResearchSection";
import DriveFileUploader from "./drive-file-uploader";
import Logs from "./logs/Logs";
import JobTasks from "./tasks/JobTasks";
import TaskAddEdit from "./tasks/TaskAddEdit";
import useTimeTracker from "../hooks/useTimeTracker";
import RippleEffect from "./RippleEffect";

//const emailBodyPreFill = `Thanks so much for your request! Our cost will be approximately 2200 + Tax at the high end because we usually end up having to survey multiple blocks to find enough rods to work back to calculate where the corners should be. If you happen to know were 2 rods are at your property corners, it could be as low as 900 + Tax, but I'd think they are extremely rare. Even knowing where some of the neighbors rods are could be a huge help if they'd had their's surveyed in recent years (like last 20 years). If you have any other information that will help me narrow it down, please reach out!`;
const selectHint = "Select one...";

const getInvoicesCheckboxValue = () => {
  const isInvoiceSentChecked = document.getElementById("invoiceSent").checked;
  const isInvoicePaidChecked = document.getElementById("invoicePaid").checked;

  return { isInvoicePaidChecked, isInvoiceSentChecked };
};

const getCountyMapLinks = (countyAppChoices) => {
  const counties = countyAppChoices;

  const links = {};
  counties.forEach((c) => {
    links[c["county"]] = {
      Appr: c["appraisalLink"],
      "Appr Map": c["appraisalMapLink"],
    };
  });

  return links;
};

const NearbyJobsModal = ({
  handleClose,
  show,
  distance,
  onDistanceChange,
  onViewClick,
  inProgress = false,
}) => (
  <Modal show={show} onHide={handleClose} centered backdrop="static">
    <Modal.Header>
      <Modal.Title>View nearby jobs:</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <h6 className="my-2">Distance to search for (in km):</h6>
      <FormControl
        size="sm"
        min={0}
        max={2}
        value={distance}
        onChange={(e) => onDistanceChange(e.target.value)}
      />

      {inProgress && (
        <ProgressBar
          className="mt-2"
          animated
          now={100}
          label="Finding nearby jobs..."
        />
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={handleClose} disabled={inProgress}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onViewClick} disabled={inProgress}>
        View
      </Button>
    </Modal.Footer>
  </Modal>
);

const EditForm = ({ appChoices }) => {
  const { user } = useAuth();
  const titleCompaniesAppChoices = useAppChoices("Title Company");
  const countyAppChoices = useAppChoices("County");

  const [form, setForm] = useState(surveyEditForm(user?.role, appChoices));
  const [fileFetchError, setFileFetchError] = useState(null);
  const [isFormLoading, setFormLoading] = useState(true);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [jobError, setJobError] = useState("");
  const [preFillData, setPreFillData] = useState({});
  const [formDisabled, setFormDisabled] = useState(true);
  const [email] = useState(localStorage.getItem("user-email"));
  const [showDueDateModal, setShowDueDateModal] = useState(false);
  const [submittingDueDate, setSubmittingDueDate] = useState(false);
  const [showStatusLogs, setShowStatusLogs] = useState(false);
  const [mapLinks, setMapLinks] = useState([]);
  const [showTaskAddEditModal, setShowTaskAddEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState();
  const [titleCompanies, setTitleCompanies] = useState([]);
  const [editingTaskIndex, setEditingTaskIndex] = useState(-1); //-1 for add else index
  const [emailBodyPreFill, setEmailBodyPreFill] = useState("");
  const [nearbyJobsModalMeta, setNearbyJobsModalMeta] = useState(null);
  const [filesToBeUploaded, setFilesToBeUploaded] = useState([]);
  const { activeTimeEntry, elapsedTime, openModal } = useTimeTracker();

  const onUploadedFilesChange = (files) => {
    setFilesToBeUploaded(files);
  };

  const { id } = useParams();

  const defaultContractMessageAppChoices = useAppChoices(
    "Default Contract Message"
  );
  // const countyResearchAppChoices = useAppChoices("County");
  const appraisalLinkAppChoices = useAppChoices("Appraisal Link");
  const appraisalMapLinkAppChoices = useAppChoices("Appraisal Map Links");

  const setupFormAndLoadData = async () => {
    const titleCompanies = titleCompaniesAppChoices;

    const date = new Date();
    date.setMilliseconds(date.getMilliseconds() + 5 * 24 * 60 * 60 * 1000);

    setAttribute(form, "dueDate", "min", getFormattedDate(date));
    setAttribute(form, "scheduledClosingDate", "min", getFormattedDate(date));
    setTitleCompanies(titleCompanies);
    setMapLinks(
      getCountyMapLinks(
        countyAppChoices,
        appraisalLinkAppChoices,
        appraisalMapLinkAppChoices
      )
    );
    setForm({ ...form });
    loadJobData();
  };

  const loadJobData = async () => {
    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.SEARCH_JOBS,
      requestBody: {
        filter: { _id: id },
        query: "",
      },
    });

    if (error) {
      setJobError(error || "Oops, something went wrong!");
      return toast.error(error);
    }

    if (response) {
      const details = response?.results[0];
      setEmailBodyPreFill(
        fillJobVariablesInText(
          defaultContractMessageAppChoices &&
            defaultContractMessageAppChoices.length > 0
            ? defaultContractMessageAppChoices[0]
            : "",
          details
        )
      );
      setAttribute(form, "files", "folderLink", details?.["driveFolder"]);
      setForm({ ...form });

      const data = {};
      Object.keys(labelToKeyMap).forEach((label) => {
        const key = labelToKeyMap[label];
        data[key] = details[labelToKeyMap[label]];
      });

      data["isThereAClosing"] = data["isThereAClosing"] ? "yes" : "no";

      if (!titleCompanies.includes(data["titleCompany"])) {
        data["titleOther"] = data["titleCompany"];
        data["titleCompany"] = "Other";
      }

      data["history"] = details["history"];
      data["driveFolder"] = details["driveFolder"];
      data["atmId"] = details["atmId"];
      data["driversLicense"] = details["driversLicense"];

      setPreFillData(data);

      fetchJobrelatedFiles(details?.driveFolder)
        .then((data) => {
          setPreFillData((prevData) => ({ ...prevData, allFiles: data }));
          setFormLoading(false);
        })
        .catch(() => {
          setPreFillData((prevData) => ({
            ...prevData,
            allFiles: [],
          }));
          setFormLoading(false);
        });
    } else {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    setupFormAndLoadData();
  }, []);

  useEffect(() => {
    if (isFormLoading || !preFillData) return;

    const atmIdElement = document.getElementById("atmId");
    const driverLicenceElement = document.getElementById("driverLicense");

    if (preFillData["atmId"] && atmIdElement) {
      atmIdElement.innerHTML = preFillData["atmId"];
    }

    if (preFillData["driversLicense"] && driverLicenceElement) {
      driverLicenceElement.innerHTML = preFillData["driversLicense"];
    }
  }, [isFormLoading, preFillData]);

  const onRequestEditFormSubmit = async (form) => {
    setButtonsDisabled(true);

    if (filesToBeUploaded.some(({ status }) => status === "pending")) {
      return toast.error("Please wait until file is being uploaded");
    }

    const formData = {
      _id: id,
    };

    if (form["titleCompany"] === "Other") {
      form["titleCompany"] = form["titleOther"];
    }

    if (form["county"] === selectHint) {
      form["county"] = "";
    }

    form["propertyNumber"] = `'${form["propertyNumber"]}`;

    Object.keys(labelToKeyMap).forEach((label) => {
      if (label === "fileNames") return;

      formData[labelToKeyMap[label]] = form[labelToKeyMap[label]];
    });

    formData["isThereAClosing"] = formData["isThereAClosing"] === "yes";

    if (!formData["fieldStaff"] || formData["fieldStaff"] === "Unassigned") {
      formData["fieldStaff"] = null;
    }
    if (!formData["draftsman"] || formData["draftsman"] === "Unassigned") {
      formData["draftsman"] = null;
    }

    if (!formData["reviewer"] || formData["reviewer"] === "Unassigned") {
      formData["reviewer"] = null;
    }

    if (!formData?.invoiceSent) {
      formData["invoiceSentDate"] = null;
    }

    if (!formData?.invoicePaid) {
      formData["invoicePaidDate"] = null;
    }

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.UPDATE_INDIVIDUAL_JOB(id),
      method: "PUT",
      requestBody: {
        ...formData,
        fileIds: filesToBeUploaded?.map(
          (fileToBeUploaded) => fileToBeUploaded?.fileId
        ),
      },
    });

    if (error) {
      setButtonsDisabled(true);
      return toast.error(error);
    }

    if (response) {
      setEmailBodyPreFill(
        fillJobVariablesInText(
          defaultContractMessageAppChoices &&
            defaultContractMessageAppChoices.length > 0
            ? defaultContractMessageAppChoices[0]
            : "",
          response
        )
      );

      const data = {};
      Object.keys(labelToKeyMap).forEach((label) => {
        const key = labelToKeyMap[label];
        data[key] = response[labelToKeyMap[label]];
      });

      data["isThereAClosing"] = data["isThereAClosing"] ? "yes" : "no";
      if (!titleCompanies.includes(data["titleCompany"])) {
        data["titleOther"] = data["titleCompany"];
        data["titleCompany"] = "Other";
      }

      data["history"] = response["history"];
      data["driveFolder"] = response["driveFolder"];

      setPreFillData({
        ...data,
        allFiles: [
          ...filesToBeUploaded
            .filter(
              (fileToBeUploaded) => fileToBeUploaded?.status === "complete"
            )
            .map((fileToBeUploaded) => ({
              id: fileToBeUploaded?.fileId,
              fileName: fileToBeUploaded?.file?.name,
              mimeType: fileToBeUploaded?.file?.mimetype,
            })),

          ...preFillData?.allFiles,
        ],
      });
      setFilesToBeUploaded([]);

      setFormLoading(false);

      toast(`Job updated successfully!`, {
        type: "success",
      });

      setButtonsDisabled(true);
    }
  };

  const onClientDifferentChange = (e) => {
    const checked = document.getElementById("isClientDifferent").checked;

    const clientName = document.getElementById("clientName");
    const clientEmail = document.getElementById("clientEmail");
    setAttribute(form, "clientName", "required", checked);
    setAttribute(form, "clientEmail", "required", checked);

    if (checked) {
      clientName.removeAttribute("disabled");
      clientEmail.removeAttribute("disabled");
    } else {
      clientName.value = "";
      clientEmail.value = "";
      clientName.setAttribute("disabled", true);
      clientEmail.setAttribute("disabled", true);
    }
    setForm({ ...form });
  };

  const onInvoiceCheckboxValuesChange = () => {
    const {
      isInvoicePaidChecked,
      isInvoiceSentChecked,
    } = getInvoicesCheckboxValue();

    const invoiceSentDate = document.getElementById("invoiceSentDate");
    const invoicePaidDate = document.getElementById("invoicePaidDate");

    if (isInvoiceSentChecked) {
      invoiceSentDate.style.display = "inline-flex";
      if (!invoiceSentDate.value) {
        const today = getFormattedDate(new Date());
        invoiceSentDate.value = today;
      }
    } else {
      invoiceSentDate.style.display = "none";
      invoiceSentDate.value = "";
    }

    if (isInvoicePaidChecked) {
      invoicePaidDate.style.display = "inline-flex";
      if (!invoicePaidDate.value) {
        const today = getFormattedDate(new Date());
        invoicePaidDate.value = today;
      }
    } else {
      invoicePaidDate.style.display = "none";
      invoicePaidDate.value = "";
    }
  };

  window["onInvoiceCheckboxValuesChange"] = onInvoiceCheckboxValuesChange;

  window["onClientDifferentChange"] = onClientDifferentChange;

  window["onRequestEditFormSubmit"] = onRequestEditFormSubmit;

  useEffect(() => {
    if (!formDisabled) {
      window.onInvoiceCheckboxValuesChange();
    }
  }, [formDisabled]);

  // Fetching all related files from a folder

  const fetchJobrelatedFiles = (driveFolder) => {
    return new Promise(async (resolve, reject) => {
      const { response, error } = await makeApiRequests({
        requestType: "read-folder",
        requestBody: { folderId: driveFolder },
      });

      if (error) {
        reject("Failure");

        return setFileFetchError(error);
      }

      resolve(response?.files);
    });
  };

  if (jobError) {
    return <NotFound text={jobError} />;
  }

  const handleClose = () => {
    setShowDueDateModal(false);
  };

  const sendAgreementContract = async () => {
    const requiredFields = [
      "dueDateFromModal",
      "valueFromModal",
      "fieldTimeFromModal",
      "emailBodyFromModal",
    ];

    for (const field of requiredFields) {
      const elem = document.getElementById(field);
      const value = elem.value;
      if (!value) {
        highlightError(elem);
        return;
      }
    }

    const dateString = new Date(
      document
        .getElementById("dueDateFromModal")
        .value.split("-")
        .join("/")
    ).toISOString();

    setSubmittingDueDate(true);

    const fieldTime = document.getElementById("fieldTimeFromModal").value;
    const value = document.getElementById("valueFromModal").value;
    const emailBody = document.getElementById("emailBodyFromModal").value;

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.SEND_CONTRACT(id),
      requestBody: {
        agreedDueDate: dateString,
        fieldTime,
        value,
        emailBody,
        _id: id,
      },
    });

    if (error) {
      setSubmittingDueDate(false);
      return toast.error(error);
    }

    if (response) {
      toast(`Contract sent successfully!`, {
        type: "success",
      });
      setShowDueDateModal(false);
      updateJobEmailDispatchedDetails(
        dateString,
        fieldTime,
        value,
        response?.UUID
      );
    } else {
      toast(response["reason"] || "Oops something went wrong!", {
        type: "error",
      });
    }

    setSubmittingDueDate(false);
  };

  const updateJobEmailDispatchedDetails = (
    dateString,
    fieldTime,
    value,
    uuid
  ) => {
    preFillData["contractStatus"] = "Contract Sent";
    preFillData["contractUuid"] = uuid;

    setPreFillData({ ...preFillData });

    document.getElementById("agreedDueDate").value = getFormattedDate(
      new Date(dateString)
    );
    document.getElementById("fieldTime").value = fieldTime;
    document.getElementById("value").value = value;
  };

  const getFormattedDate = (date) => {
    var dd = date.getDate();
    var mm = date.getMonth() + 1; //January is 0!
    var yyyy = date.getFullYear();
    if (dd < 10) {
      dd = "0" + dd;
    }
    if (mm < 10) {
      mm = "0" + mm;
    }

    return yyyy + "-" + mm + "-" + dd;
  };

  const DateModal = () => {
    return (
      <Modal
        show={showDueDateModal}
        onHide={handleClose}
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Please confirm the following fields:</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preFillData &&
            (preFillData["contractStatus"] === "Contract Sent" ||
              preFillData["contractStatus"] === "Contract Viewed") && (
              <Alert variant="warning">
                Contract already sent, please only proceed if you are renewing
                it
              </Alert>
            )}
          <h6 className="my-2">Agreed due date</h6>
          <TextInput
            id="dueDateFromModal"
            variant="date"
            required={true}
            value={preFillData["agreedDueDate"]}
            style={{ fontSize: "10px" }}
          />
          <h6 className="my-2">Field Time</h6>
          <NumberInput
            id="fieldTimeFromModal"
            required={true}
            value={preFillData["fieldTime"]}
          />
          <h6 className="my-2">Value</h6>
          <NumberInput
            id="valueFromModal"
            required={true}
            value={preFillData["value"]}
          />
          <h6 className="my-2">Email Body</h6>
          <TextInput
            textArea
            id="emailBodyFromModal"
            required={true}
            value={emailBodyPreFill}
            rows={5}
            style={{ fontSize: "10px" }}
          />
          {submittingDueDate && (
            <ProgressBar
              className="mt-2"
              animated
              now={100}
              label="Sending contract..."
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submittingDueDate}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={sendAgreementContract}
            disabled={submittingDueDate}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const onSendAgreementClick = () => {
    setShowDueDateModal(true);
  };

  const StatusLogsModal = () => {
    return (
      <Modal
        show={showStatusLogs}
        onHide={() => setShowStatusLogs(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Status Change logs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Logs logs={preFillData?.history} />
        </Modal.Body>
      </Modal>
    );
  };

  const onTaskAddClick = () => {
    setShowTaskAddEditModal(true);
  };

  const onTaskEditClick = (task, index) => {
    setEditingTask(task);
    setEditingTaskIndex(index);

    setShowTaskAddEditModal(true);
  };

  const onTaskDelete = (index) => {
    if (index !== -1) {
      preFillData["tasks"].splice(index, 1);
      setPreFillData({ ...preFillData });
    }
  };

  const onTaskStatusUpdate = (status, index) => {
    if (index !== -1) {
      preFillData["tasks"][index]["status"] = status;
      setPreFillData({ ...preFillData });
    }
  };

  const onTaskAddEdit = (task, existingIndex) => {
    toast(
      existingIndex === -1
        ? `Task added successfully!`
        : "Task updated successfully",
      {
        type: "success",
      }
    );

    if (existingIndex === -1) {
      preFillData["tasks"] = [task, ...(preFillData["tasks"] || [])];
    } else {
      preFillData["tasks"][existingIndex] = task;
    }

    setPreFillData({ ...preFillData });

    setEditingTask(null);
    setEditingTaskIndex(-1);
    setShowTaskAddEditModal(false);
  };

  const findNearbyJobs = async () => {
    if (!nearbyJobsModalMeta.distance)
      return toast.error("Please provide a distance in km!");

    const distanceInNumber = Number(nearbyJobsModalMeta.distance);
    if (distanceInNumber <= 0) {
      return toast.error("Distance should be greater than 0");
    }

    setNearbyJobsModalMeta({ ...nearbyJobsModalMeta, inProgress: true });

    const { response, error } = await makeRESTApiRequests({
      endpoint: ENDPOINTS.FIND_NEARBY_JOBS(id),
      requestBody: {
        latitude: preFillData["latitude"],
        longitude: preFillData["longitude"],
        radius: distanceInNumber,
      },
    });

    if (error) {
      setNearbyJobsModalMeta({ ...nearbyJobsModalMeta, inProgress: false });
      return toast.error(error);
    }

    if (response) {
      const nearbyJobs = response;
      if (nearbyJobs?.length === 0) {
        setNearbyJobsModalMeta({ ...nearbyJobsModalMeta, inProgress: false });
        return toast.error("No jobs found in the specified radius!");
      }

      window.open(
        createMapsLinkFromJobs(
          nearbyJobs,
          15,
          {
            latitude: preFillData["latitude"],
            longitude: preFillData["longitude"],
          },
          distanceInNumber,
          true
        ),
        "_blank"
      );
      setNearbyJobsModalMeta(null);
    }
  };

  return (
    <Container
      fluid
      className="py-2"
      // style={{ height: "100vh", overflowY: "auto" }}
    >
      <Card>
        <Card.Body className="p-2">
          {isFormLoading && <Loader />}
          {!isFormLoading && form && preFillData && (
            <>
              <Row>
                <Col md={6} xs={12}>
                  <ResearchSection
                    researchToShow={
                      countyAppChoices[
                        countyAppChoices.findIndex(
                          (countyAppChoice) =>
                            preFillData["county"] === countyAppChoice?.county
                        )
                      ]?.researchText
                    }
                    county={preFillData["county"]}
                  />
                </Col>
                <Col md={6} xs={12}>
                  <JobTasks
                    tasks={preFillData["tasks"]}
                    onAddClick={onTaskAddClick}
                    onEditClick={onTaskEditClick}
                    onStatusUpdate={onTaskStatusUpdate}
                    onTaskDelete={onTaskDelete}
                    jobId={id}
                  />
                </Col>
              </Row>
              <TaskAddEdit
                onHide={() => {
                  setEditingTask(null);
                  setEditingTaskIndex(-1);
                  setShowTaskAddEditModal(false);
                }}
                show={showTaskAddEditModal}
                onTaskAddEdit={onTaskAddEdit}
                editingTaskIndex={editingTaskIndex}
                editingTask={editingTask}
                jobId={id}
                tasks={preFillData["tasks"]}
              />
              <hr className="my-0" />
              {user?.role !== "fieldStaff" && (
                <>
                  <div className="d-flex pl-2">
                    <div className="flex-grow-1 text-left">
                      {preFillData["contractStatus"] && (
                        <>
                          <b className="tiny-text">Contract Status: </b>
                          <Badge variant="warning" className="tiny-text">
                            {preFillData["contractStatus"] || "Not Sent"}
                          </Badge>
                          {preFillData["urgent"] && (
                            <Badge variant="danger" className="tiny-text ml-1">
                              Urgent
                            </Badge>
                          )}
                        </>
                      )}
                    </div>

                    <div className="text-right">
                      {["Admin", "Manager"].includes(user?.role) && (
                        <>
                          {preFillData["contractUuid"] && (
                            <Button
                              size="sm"
                              variant="primary"
                              className=" py-0 px-1 mr-1"
                            >
                              <Link
                                target="_blank"
                                to={`/agreement/preview/${
                                  preFillData["contractUuid"]
                                }`}
                                className=" text-white tiny-text"
                              >
                                <Eye size={12} className="mr-2" />
                                Preview Agreement
                              </Link>
                            </Button>
                          )}

                          {activeTimeEntry && activeTimeEntry ? (
                            <Button
                              size="sm"
                              variant="primary"
                              className=" py-0 px-1 mr-1 tiny-text"
                              onClick={openModal}
                            >
                              <RippleEffect size={20} />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="primary"
                              className=" py-0 px-1 mr-1 tiny-text"
                              onClick={openModal}
                            >
                              <ClockFill size={12} className="mr-2" />
                              Clock In
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="primary"
                            onClick={onSendAgreementClick}
                            className=" py-0 px-1 mr-1 tiny-text"
                          >
                            <EnvelopeFill size={12} className="mr-2" />
                            Send Agreement
                          </Button>
                        </>
                      )}

                      {preFillData["county"] &&
                        mapLinks[preFillData["county"]] && (
                          <>
                            {mapLinks[preFillData["county"]]["Appr"] && (
                              <a
                                href={mapLinks[preFillData["county"]][
                                  "Appr"
                                ].replace(
                                  "{{appraisalId}}",
                                  preFillData["propertyAppraisalId"]
                                )}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button
                                  size="sm"
                                  className="py-0 px-1 mr-1 tiny-text"
                                >
                                  Open Appr. Link
                                </Button>
                              </a>
                            )}
                            {mapLinks[preFillData["county"]]["Appr Map"] && (
                              <a
                                href={mapLinks[preFillData["county"]][
                                  "Appr Map"
                                ].replace(
                                  "{{appraisalId}}",
                                  preFillData["propertyAppraisalId"]
                                )}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Button
                                  size="sm"
                                  className="py-0 px-1 mr-1 tiny-text"
                                >
                                  Open Appr. Map Link
                                </Button>
                              </a>
                            )}
                          </>
                        )}

                      {preFillData["latitude"] && preFillData["longitude"] && (
                        <>
                          <a
                            href={`https://maps.google.com/?q=${
                              preFillData["latitude"]
                            },${preFillData["longitude"]}
                                `}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button
                              size="sm"
                              className="py-0 px-1 mr-1 tiny-text"
                            >
                              Open Google Map
                            </Button>
                          </a>
                          <Button
                            onClick={() =>
                              setNearbyJobsModalMeta({ distance: 0.5 })
                            }
                            size="sm"
                            className="py-0 px-1 mr-1 tiny-text"
                          >
                            View Nearby Jobs
                          </Button>
                          <NearbyJobsModal
                            onViewClick={findNearbyJobs}
                            inProgress={nearbyJobsModalMeta?.inProgress}
                            handleClose={() => setNearbyJobsModalMeta(null)}
                            show={nearbyJobsModalMeta !== null}
                            distance={nearbyJobsModalMeta?.distance}
                            onDistanceChange={(distance) =>
                              setNearbyJobsModalMeta({
                                ...nearbyJobsModalMeta,
                                distance,
                              })
                            }
                          />
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setFormDisabled(false);
                          setTimeout(() => onClientDifferentChange(), 10);
                        }}
                        className=" py-0 px-1 mr-1 tiny-text"
                        disabled={!formDisabled || buttonsDisabled}
                      >
                        <Pen size={12} className="mr-2" />
                        {formDisabled ? "Edit" : "Edit Mode Active"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => {
                          setShowStatusLogs(true);
                        }}
                        className="py-0 px-1 mr-1 tiny-text"
                      >
                        <FileFill size={12} className="mr-2" />
                        Logs
                      </Button>
                    </div>

                    {StatusLogsModal()}
                  </div>

                  <hr className="mt-1 mb-0" />
                  {DateModal()}
                </>
              )}
              <FormGenerator
                formJson={form}
                formValues={{ "View survey": preFillData }}
                formDisabled={formDisabled}
              />
              <div className="mb-5 px-4">
                <DriveFileUploader
                  title={"Files"}
                  filesToBeUploaded={filesToBeUploaded}
                  onUploadedFilesChange={onUploadedFilesChange}
                  preFilledFiles={preFillData?.allFiles}
                  deletable={false}
                  hideUploader={formDisabled}
                  showFolder={true}
                  folderId={preFillData?.driveFolder}
                  showError={fileFetchError}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditForm;
